import React, { useContext, useMemo, useState } from "react";
import { Menu, Moon, Sun, User, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Select } from "antd";
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../context/AppContextProvider";
import Logo from "../../assets/images/logo/ps-logo.png";
import LanguageOption from "../../utils/language-dropdown";
import { normalizeLanguageCode } from "../../utils/i18nLanguage";
import ElectionModePlaybookModal from "./ElectionModePlaybookModal";
import ConstituencyPlaybookModal from "../ConstituencyPlaybookModal";
import SirReportModal from "../SirReportModal";
import ReportsMenu from "../ReportsMenu";
import useReportsAccess from "../../hooks/useReportsAccess";

const ElectionModeNavbar = ({
  title,
  logoUrl,
  sidebarOpen,
  onToggleSidebar,
  filters,
  setFilters,
  filterData,
  parties = [],
  electionModeType,
  urbanRural,
}) => {
  const { themes, setThemes, handleLogout } = useContext(AppContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [profileOpen, setProfileOpen] = useState(false);
  const [showPlaybookModal, setShowPlaybookModal] = useState(false);
  const [showPiuPlaybookModal, setShowPiuPlaybookModal] = useState(false);
  const [showSirReportModal, setShowSirReportModal] = useState(false);
  const user = JSON.parse(localStorage.getItem("user"));
  const { hasPiuPlaybook, hasElectionModePlaybook, sirReport } = useReportsAccess();

  const toggleTheme = () => {
    const newTheme = themes === "darkMode" ? "lightMode" : "darkMode";
    setThemes(newTheme);
    localStorage.setItem("themes", newTheme);
  };

  const handleRegionChange = (value) => {
    setFilters?.((prev) => ({ ...prev, region: value || "", district: "", constituency: "" }));
  };
  const handleDistrictChange = (value) => {
    setFilters?.((prev) => ({ ...prev, district: value || "", constituency: "" }));
  };
  const handleConstituencyChange = (value) => {
    setFilters?.((prev) => ({ ...prev, constituency: value || "" }));
  };
  const handlePartyChange = (value) => {
    setFilters?.((prev) => ({ ...prev, party: value }));
  };

  const handleLanguageChange = (value) => {
    localStorage.setItem("selectedLanguage", value);
    // Normalize legacy codes (e.g. "marathi" → "mr") so the same resources load
    // instantly as on refresh, instead of an unmapped key with no translations.
    i18next.changeLanguage(normalizeLanguageCode(value));
  };

  // When a region is selected, restrict district options to the districts that
  // belong to that region (BJP Kshetra). Otherwise show all districts.
  const districtOptions = useMemo(() => {
    const allDistricts = filterData?.filters?.districts || [];
    const regionMap = filterData?.filters?.regionDistrictMap || {};
    const list =
      filters?.region && regionMap[filters.region]
        ? regionMap[filters.region]
        : allDistricts;
    return list.map((d) => ({ value: d, label: d }));
  }, [filterData, filters?.region]);

  // Assembly mode replaces Constituency/District with Ward (Urban) or
  // Village + Block (Rural). The underlying filter params (`district` /
  // `constituency`) stay the same on purpose — only the visible label and
  // which selects are shown change, so the existing filtering API is reused
  // as-is (Village -> constituency param, Block -> district param).
  const isAssembly = electionModeType === "assembly";
  const isRuralAssembly = isAssembly && urbanRural === "Rural";
  const isUrbanAssembly = isAssembly && (urbanRural === "Urban" || !urbanRural);

  const districtLabel = isRuralAssembly
    ? t("ElectionMode.block")
    : t("ElectionMode.district");
  const constituencyLabel = isRuralAssembly
    ? t("ElectionMode.village")
    : isUrbanAssembly
    ? t("ElectionMode.ward")
    : t("ElectionMode.constituency");

  // Playbook lookups key off constituency (state/parliamentary scope) or the
  // block selector, which in Rural Assembly mode is surfaced as `district`.
  const playbookConstituency = filters?.constituency || "";
  const playbookBlock = isRuralAssembly ? filters?.district || "" : "";

  const reportItems = [
    hasElectionModePlaybook && {
      key: "election-mode-playbook",
      label: "Election Mode Playbook",
      onClick: () => setShowPlaybookModal(true),
    },
    hasPiuPlaybook && {
      key: "piu-playbook",
      label: "PIU Playbook",
      onClick: () => setShowPiuPlaybookModal(true),
    },
    sirReport && {
      key: "sir-report",
      label: "SIR Report",
      onClick: () => setShowSirReportModal(true),
    },
  ].filter(Boolean);

  return (
    <>
    <nav className="glass-shell relative rounded-[22px] px-3 md:px-4 py-2 flex items-center justify-between gap-3 shadow-lg">
  {/* Left Section */}
  <div className="flex items-center gap-2 z-10 flex-shrink-0">
    <button
      type="button"
      onClick={onToggleSidebar}
      className="w-9 h-9 rounded-full bg-slate-700/85 text-black flex items-center justify-center hover:bg-slate-600 transition-colors"
      title={sidebarOpen ? "Close sidebar" : "Open sidebar"}
    >
      <Menu size={18} />
    </button>

    <button
      type="button"
      onClick={() => navigate("/election-mode")}
      className="hidden sm:flex items-center"
      title="Election overview"
    >
      <div className="flex items-center gap-2">
        <img src={Logo} alt="Polstrat" className="h-8 w-auto object-contain" />
        {logoUrl && (
          <img src={logoUrl} alt="Election Logo" className="h-8 max-w-[90px] w-auto object-contain" />
        )}
        <span className="font-bold text-black text-xs md:text-sm line-clamp-2 max-w-[120px]">
          {title}
        </span>
      </div>
    </button>
  </div>

  {/* Center — Top Filters */}
  {filters && setFilters && (
    <div className="flex items-center gap-1.5 flex-1 min-w-0">
      <Select
        allowClear
        showSearch
        size="small"
        placeholder={t("ElectionMode.region")}
        value={filters.region || undefined}
        onChange={handleRegionChange}
        optionFilterProp="label"
        options={(filterData?.filters?.regions || []).map((r) => ({ value: r, label: r }))}
        getPopupContainer={() => document.body}
        dropdownStyle={{ zIndex: 10001 }}
        className="flex-1 min-w-0"
        style={{ fontSize: 11 }}
      />
      {/* District (or "Block" for Rural Assembly). Hidden for Urban Assembly
          since Ward alone replaces Constituency/District/Parliamentary. */}
      {!isUrbanAssembly && (
        <Select
          allowClear
          showSearch
          size="small"
          placeholder={districtLabel}
          value={filters.district || undefined}
          onChange={handleDistrictChange}
          optionFilterProp="label"
          options={districtOptions}
          getPopupContainer={() => document.body}
          dropdownStyle={{ zIndex: 10001 }}
          className="flex-1 min-w-0"
          style={{ fontSize: 11 }}
        />
      )}
      {/* Constituency (or "Village" for Rural / "Ward" for Urban Assembly). */}
      <Select
        allowClear
        showSearch
        size="small"
        placeholder={constituencyLabel}
        value={filters.constituency || undefined}
        onChange={handleConstituencyChange}
        optionFilterProp="label"
        options={(filterData?.filters?.constituencies || []).map((c) => ({ value: c, label: c }))}
        getPopupContainer={() => document.body}
        dropdownStyle={{ zIndex: 10001 }}
        className="flex-1 min-w-0"
        style={{ fontSize: 11 }}
      />
      <Select
        showSearch
        size="small"
        placeholder={t("ElectionMode.party")}
        value={filters.party || undefined}
        onChange={handlePartyChange}
        optionFilterProp="label"
        options={(parties.length ? parties : ["BJP"]).map((p) => ({ value: p, label: p }))}
        getPopupContainer={() => document.body}
        dropdownStyle={{ zIndex: 10001 }}
        className="flex-1 min-w-0"
        style={{ fontSize: 11 }}
      />
    </div>
  )}

  {/* Right Section */}
  <div className="bg-slate-700/80 backdrop-blur-sm rounded-[2rem] px-3 py-2 flex items-center gap-2 shadow-md h-[2.5rem] z-10 flex-shrink-0">
    {reportItems.length > 0 && (
      <>
        <ReportsMenu
          items={reportItems}
          className="bg-gradient-to-r from-orange-500 to-amber-400 text-white text-[11px] font-semibold px-3 py-1.5 rounded-full shadow-md border border-orange-200/40 whitespace-normal z-[9999]"
        />
        <div className="w-px h-5 bg-slate-400/50"></div>
      </>
    )}

    {/* Language selector (same as Navbar2) */}
    <div className="nav-right-item">
      <LanguageOption onChange={handleLanguageChange} />
    </div>

    <div className="w-px h-5 bg-slate-400/50"></div>

    <button
      type="button"
      onClick={toggleTheme}
      className="w-7 h-7 rounded-full text-white flex items-center justify-center hover:bg-white/10 transition-colors"
      title="Toggle theme"
    >
      {themes === "darkMode" ? <Moon size={16} /> : <Sun size={16} />}
    </button>

    <div className="relative">
      <button
        type="button"
        onClick={() => setProfileOpen((value) => !value)}
        className="w-7 h-7 rounded-full bg-slate-600 text-white flex items-center justify-center hover:bg-slate-500 transition-colors"
        title="Profile"
      >
        <User size={15} />
      </button>

      {profileOpen && (
        <div className="absolute right-0 mt-3 w-56 rounded-xl bg-white shadow-2xl border border-slate-200 p-3 text-slate-800">
          <div className="text-sm font-semibold truncate">
            {[user?.firstName, user?.lastName]
              .filter(Boolean)
              .join(" ") ||
              user?.email ||
              "User"}
          </div>

          <div className="text-xs text-slate-500 truncate mt-0.5">
            {user?.email || user?.phone || ""}
          </div>

          <button
            type="button"
            onClick={handleLogout}
            className="mt-3 w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            <LogOut size={15} />
            Logout
          </button>
        </div>
      )}
    </div>
  </div>
</nav>

<ElectionModePlaybookModal
  open={showPlaybookModal}
  onClose={() => setShowPlaybookModal(false)}
  electionModeType={electionModeType}
  selectedConstituency={playbookConstituency}
  selectedBlock={playbookBlock}
/>

<ConstituencyPlaybookModal
  open={showPiuPlaybookModal}
  onClose={() => setShowPiuPlaybookModal(false)}
/>

<SirReportModal
  open={showSirReportModal}
  onClose={() => setShowSirReportModal(false)}
  sirReportId={sirReport?._id}
/>
    </>
  );
};

export default ElectionModeNavbar;
